import apiClient from 'commons/api/client/apiClient';
import store from 'commons/store/Store';
import designUploadServiceV2 from '../designUploadService/designUploadServiceV2';
import localeService from 'commons/localeService/localeService';
import type { AxiosResponse } from 'axios';
import type {
  Composition,
  HotspotConfigs,
} from '@spreadgroup/common-partner-lib/composition';
import type {
  AppearanceGroup,
  CompositionSpecificCategoriesTree,
  PublishingCategoriesTree,
} from 'commons/composition/compositionCategoryHelper';
import type { PublishingScope } from 'commons/publishing/publishingScope';
import type { Appearance, PublishingCoreConfig } from './configTypes';
import type { InventoryTranslations } from './translationTypes';
import type {
  CompositionList,
  CompositionListQuery,
  CompositionListWithSellablesQuery,
  CompositionUpdate,
  PlacementPreviews,
  PosStateCounts,
  PosStateCountsQuery,
} from './compositionTypes';
import type {
  AiOptOutValue,
  DesignerPropertiesData,
  PublishingCoreShop,
  PublishingLimit,
} from './partnerTypes';
import { withPrintAreaDesigns } from 'commons/composition/multiPrintAreaCreation';

export type {
  AiOptOutValue,
  DesignerGroup,
  DesignerProperties,
  DesignerPropertiesData,
  PublishingCoreShop,
  PublishingLimit,
} from './partnerTypes';
export type {
  CompositionList,
  CompositionListQuery,
  CompositionListWithSellablesQuery,
  CompositionUpdate,
  PlacementPreview,
  PlacementPreviews,
  PosStateCounts,
  PosStateCountsQuery,
} from './compositionTypes';
export type {
  Appearance,
  ImageTemplates,
  PublishingCoreConfig,
} from './configTypes';
export type {
  InventoryTranslation,
  InventoryTranslations,
  ProductTypeTranslation,
} from './translationTypes';

export const aiOptOutKey = 'COMPOSITION_AI_CUSTOMIZATION';

export default {
  /**
   * Unlike every other method here this resolves with the raw axios response
   * instead of its `data`. Callers ignore the value.
   */
  async createCompositionFromDesignIdWithScope(
    designId: number,
    scope?: PublishingScope,
  ): Promise<AxiosResponse<Composition>> {
    return await apiClient.request({
      method: 'POST',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/fromDesign`,
      params: {
        designId,
        scope: scope?.key,
        creator: 'partnerarea',
      },
    });
  },
  /**
   * `/compositions/fromDesign` takes a single `designId`, so the other designs are added in a
   * follow-up update. A failed update deletes the composition again. See
   * `docs/publishing/multi-print-area-creation.md` §4.
   */
  async createCompositionFromDesignIdsWithScope({
    designIds,
    mainDesignId,
    hotspotConfigs,
    scope,
  }: {
    designIds: number[];
    mainDesignId: number;
    hotspotConfigs: HotspotConfigs;
    scope?: PublishingScope;
  }): Promise<Composition> {
    const { data: created } = await this.createCompositionFromDesignIdWithScope(
      mainDesignId,
      scope,
    );

    try {
      return await this.updateComposition(
        withPrintAreaDesigns({
          composition: created,
          designIds,
          mainDesignId,
          hotspotConfigs,
        }),
      );
    } catch (updateError) {
      await this.deleteComposition(created.id).catch((deleteError) =>
        console.error(
          `Failed to delete composition ${created.id} after its update failed`,
          deleteError,
        ),
      );
      throw updateError;
    }
  },
  async createCompositionFromDesignUploadUrlWithScope(
    uploadUrl: string,
    scope?: PublishingScope,
  ): Promise<AxiosResponse<Composition>> {
    const { designId, rejectionReason, rejectionCodes } =
      await designUploadServiceV2.waitForUploadProcessing(uploadUrl);
    if (rejectionReason) {
      return Promise.reject({ rejectionReason, rejectionCodes });
    }
    return this.createCompositionFromDesignIdWithScope(designId, scope);
  },
  async getCompositions(
    options: CompositionListQuery,
  ): Promise<CompositionList> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/list`,
      params: options,
    });
    return data;
  },
  /**
   * The same list as `getCompositions`, but with each composition's **sellables** resolved.
   *
   * publishing-core has two list endpoints over one query: `/compositions/list` returns no
   * `sellables` and no `defaultValues`, so it can neither count a composition's products nor derive
   * its mode (`docs/publishing/composition-mode-contract.md` §5 — everything from it reads as
   * UNSUPPORTED). `/compositions` resolves them, and that is the expensive half. Use
   * `getCompositions` wherever the products do not matter.
   *
   * `shopId` narrows the list to what is **assigned to** that shop, unpublished included. It takes a
   * separate code path there which supports neither `query` nor `posState`, only paging; `scope` stays
   * required. Read in publishing-core on 2026-08-27.
   */
  async getCompositionsWithSellables(
    options: CompositionListWithSellablesQuery,
  ): Promise<CompositionList> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions`,
      params: options,
    });
    return data;
  },
  async getComposition(compositionId: string): Promise<Composition> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/${compositionId}`,
    });
    return data;
  },
  async getCompositionCategories(
    compositionId: string,
  ): Promise<CompositionSpecificCategoriesTree> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/${compositionId}/categories`,
    });
    return data;
  },
  async getCompositionPlacementPreviews(
    compositionId: string,
    productTypeId: number,
    hotspotTags: string[],
    appearanceId?: number,
  ): Promise<PlacementPreviews> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/${compositionId}/previews`,
      params: {
        productTypeId,
        appearanceId,
        hotspotTags: hotspotTags.join(','),
      },
    });
    return data;
  },
  async getPosStateCounts(
    options: PosStateCountsQuery,
  ): Promise<PosStateCounts> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/counts`,
      params: options,
    });
    return data;
  },
  /** The response body is undocumented and not consumed. */
  async deleteComposition(compositionId: string): Promise<void> {
    const { data } = await apiClient.request({
      method: 'DELETE',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/${compositionId}`,
    });
    return data;
  },
  async updateComposition(
    composition: CompositionUpdate,
  ): Promise<Composition> {
    const { data } = await apiClient.request({
      method: 'PUT',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/${composition.id}`,
      data: composition,
    });
    return data;
  },
  /** Computes the default shape without persisting it, so the caller decides what to write. */
  async getResetComposition(compositionId: string): Promise<Composition> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/${compositionId}/reset`,
    });
    return data;
  },
  async updateCompositionSellables({
    composition,
    payload,
    preview,
  }: {
    composition: Pick<Composition, 'id'>;
    payload: unknown;
    preview?: boolean;
  }): Promise<Composition> {
    const { data } = await apiClient.request({
      method: 'PUT',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/${composition.id}/updateSellables`,
      data: payload,
      params: {
        preview,
      },
    });
    return data;
  },
  async getTranslations(): Promise<InventoryTranslations> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: 'api/pubc/translations/inventory',
      params: {
        language: localeService.getLanguage(),
      },
    });
    return data;
  },
  /** The partner wide category tree — not tied to a single composition. */
  async getCategories(): Promise<PublishingCategoriesTree> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/categories`,
    });
    return data;
  },
  async getConfig(): Promise<PublishingCoreConfig> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/config`,
    });
    return data;
  },
  async getAppearanceGroups(): Promise<AppearanceGroup[]> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/config/appearanceGroups`,
    });
    return data;
  },
  async getAppearances(): Promise<Appearance[]> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/config/appearances`,
    });
    return data;
  },
  async getShops(): Promise<PublishingCoreShop[]> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/shops`,
    });
    return data;
  },
  async registerShop(shopId: number): Promise<PublishingCoreShop> {
    const { data } = await apiClient.request({
      method: 'POST',
      url: `api/pubc/partner/${store.state.user.data.id}/shops`,
      data: { shopId },
    });
    return data;
  },
  /** The response body is undocumented and not consumed. */
  async updateCompositionAttributes(
    composition: Pick<Composition, 'id'>,
    attributes: Record<string, string>,
  ): Promise<void> {
    const { data } = await apiClient.request({
      method: 'PUT',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/${composition.id}/attributes`,
      data: attributes,
    });
    return data;
  },
  /**
   * Takes a `PUBLISHING_SCOPE` key (e.g. `PUBLISHING_SCOPE.MP.key`), not the
   * scope object.
   */
  async unpublishAllScopeCompositions(scope: string): Promise<void> {
    const { data } = await apiClient.request({
      method: 'PUT',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions/unpublish`,
      params: { scope },
    });
    return data;
  },
  /**
   * Takes a `PUBLISHING_SCOPE` key (e.g. `PUBLISHING_SCOPE.MP.key`), not the
   * scope object.
   */
  async deleteAllScopeCompositions(scope: string): Promise<void> {
    const { data } = await apiClient.request({
      method: 'DELETE',
      url: `api/pubc/partner/${store.state.user.data.id}/compositions`,
      params: { scope },
    });
    return data;
  },
  async getPublishingLimit(): Promise<PublishingLimit> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/publishingLimit`,
    });
    return data;
  },
  async getDesignerProperties(): Promise<DesignerPropertiesData> {
    const { data } = await apiClient.request({
      method: 'GET',
      url: `api/pubc/partner/${store.state.user.data.id}/designerProperties`,
    });
    return {
      designerProperties: {
        ...data.properties,
        designerGroup: data.designerGroup,
        hasDefaultGroup: data.hasDefaultGroup,
        dateGroupChanged: data.dateGroupChanged,
      },
    };
  },
  /** The response body is undocumented and not consumed. */
  async setAiOptOut(value: AiOptOutValue): Promise<void> {
    const { data } = await apiClient.request({
      method: 'PUT',
      url: `api/pubc/partner/${store.state.user.data.id}/settings`,
      data: {
        value,
        key: aiOptOutKey,
      },
    });
    return data;
  },
};
